import { useEffect, useRef, useState } from "react";
import { swrJson, clearApiCache } from "@/lib/swr";
import { Link } from "react-router-dom";
import { useTenant } from "@/lib/tenant";
import { latar } from "@/lib/progress";
import { ReportButton } from "@/components/ReportButton";

/** Chrome aplikasi: identitas tenant, kesegaran data, akses cepat ke Sync, dan menu akun.
 *  Angka detail per vendor tetap di SyncBar halaman — di sini hanya status hidup/basi. */
export function Topbar({ ciut, onAlihSidebar }: { ciut?: boolean; onAlihSidebar?: () => void } = {}) {
  // Identitas tenant tidak lagi diambil ulang di sini: satu sumber, TenantProvider.
  const { tenant, user, permissions } = useTenant();
  const [fresh, setFresh] = useState<any>(null);
  const [menuBuka, setMenuBuka] = useState(false);
  const [keluarBusy, setKeluarBusy] = useState(false);
  const [keluarErr, setKeluarErr] = useState<string | null>(null);
  const akunRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    latar(() => swrJson("/api/v1/datasources/freshness", (d) => { if (d) setFresh(d); }));
  }, []);

  // Menu akun ditutup oleh klik di luar dan oleh Escape. Tanpa ini menu tersangkut
  // terbuka saat pengguna pindah halaman lewat sidebar.
  useEffect(() => {
    if (!menuBuka) return;
    const diLuar = (e: PointerEvent) => {
      if (akunRef.current && !akunRef.current.contains(e.target as Node)) setMenuBuka(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setMenuBuka(false); };
    document.addEventListener("pointerdown", diLuar);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("pointerdown", diLuar); document.removeEventListener("keydown", esc); };
  }, [menuBuka]);

  async function keluar() {
    setKeluarBusy(true);
    setKeluarErr(null);
    try {
      const r = await fetch("/api/v1/auth/logout", { method: "POST", credentials: "same-origin" });
      // Jangan pura-pura berhasil: kalau cookie tidak jadi dihapus server, sesi masih
      // hidup dan memindahkan pengguna ke /login hanya menyembunyikan kegagalan.
      if (!r.ok) throw new Error(`Gagal keluar (${r.status})`);
      clearApiCache();
    } catch (e) {
      setKeluarBusy(false);
      setKeluarErr(e instanceof Error ? e.message : "Gagal keluar");
      return;
    }
    // Navigasi keras, bukan router: TenantProvider menyimpan tenant, permission, dan
    // navigasi di state React. Pindah lewat SPA menyisakan data tenant lama di memori.
    window.location.assign("/login");
  }

  const err = fresh?.jobs?.editing?.lastStatus === "error" || fresh?.jobs?.social?.lastStatus === "error";
  const initials = (user?.name ?? "?").split(" ").slice(0, 2).map((x: string) => x[0]).join("").toUpperCase();
  const bolehSync = permissions.includes("manage_users");
  return (
    <header className="avio-top">
      {onAlihSidebar && (
        <button type="button" className="avio-burger" onClick={onAlihSidebar}
          aria-expanded={!ciut} aria-controls="avio-sidebar"
          aria-label={ciut ? "Perlebar menu samping" : "Ciutkan menu samping"}
          title={ciut ? "Perlebar menu samping" : "Ciutkan menu samping"}>
          <i className="fa-solid fa-bars" />
        </button>
      )}
      <div style={{ minWidth: 0 }}>
        <div className="avio-top__title" style={{ fontSize: "var(--text-md)", fontWeight: "var(--weight-semi)" as any }}>
          {tenant?.name ?? "—"}
        </div>
        <div className="avio-top__sub">
          {user?.email ?? ""}
          {tenant?.subscription ? ` · ${tenant.subscription.packageName}` : ""}
        </div>
      </div>
      <div className="avio-top__actions">
        {/* Label "data N mnt lalu" DIHAPUS atas permintaan: angkanya sudah ada di
            SyncBar tiap halaman dan di halaman Sync Data, jadi di sini hanya
            menambah kebisingan. Status ERROR sengaja DIPERTAHANKAN — ini satu-
            satunya tempat ingestion yang gagal terlihat tanpa membuka halaman
            lain, dan menyembunyikannya berarti kegagalan sinkron jadi senyap. */}
        {err && (
          <Link className="avio-chip" data-shape="pill" to="/a/sync"
            style={{ background: "var(--status-danger-bg)", color: "var(--status-danger)", textDecoration: "none" }}
            title="Ingestion terakhir gagal — buka Sync Data">
            sinkron error
          </Link>
        )}
        <ReportButton />
        {bolehSync && (
          <Link className="avio-btn" data-variant="secondary" data-size="sm" to="/a/sync">
            <i className="fa-solid fa-rotate" /> Sync
          </Link>
        )}

        <div ref={akunRef} style={{ position: "relative", flex: "none" }}>
          <button type="button" onClick={() => setMenuBuka((v) => !v)}
            aria-haspopup="menu" aria-expanded={menuBuka} aria-label="Menu akun"
            title={user?.name ?? "Akun"}
            style={{
              width: 30, height: 30, borderRadius: "50%", display: "grid", placeItems: "center",
              background: "var(--color-action-soft)", color: "var(--brand-700)",
              fontSize: "var(--text-sm)", fontWeight: "var(--weight-semi)" as any,
              border: 0, padding: 0, cursor: "pointer",
            }}>{initials}</button>

          {menuBuka && (
            <div role="menu" style={{
              position: "absolute", top: "calc(100% + 8px)", right: 0, minWidth: 220, zIndex: 60,
              background: "var(--color-surface)", border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-md)", boxShadow: "0 18px 42px -18px rgba(4, 12, 32, .45)",
              overflow: "hidden", textAlign: "left",
            }}>
              <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--color-border)" }}>
                <div className="avio-t-sm" style={{ fontWeight: "var(--weight-semi)" as any }}>{user?.name ?? "Pengguna"}</div>
                <div className="avio-t-xs avio-muted" style={{ marginTop: 2, wordBreak: "break-all" }}>{user?.email ?? ""}</div>
                {tenant?.name && <div className="avio-t-xs avio-muted" style={{ marginTop: 2 }}>{tenant.name}</div>}
              </div>

              {keluarErr && (
                <div className="avio-t-xs" style={{ padding: "8px 14px", color: "var(--status-danger)" }}>{keluarErr}</div>
              )}

              <button type="button" role="menuitem" onClick={keluar} disabled={keluarBusy}
                style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%",
                  padding: "10px 14px", background: "none", border: 0, cursor: keluarBusy ? "wait" : "pointer",
                  font: "inherit", fontSize: "var(--text-sm)", color: "var(--color-text)", textAlign: "left",
                }}>
                <i className={`fa-solid ${keluarBusy ? "fa-circle-notch fa-spin" : "fa-arrow-right-from-bracket"}`}
                  style={{ width: 14, color: "var(--color-text-muted)" }} />
                {keluarBusy ? "Keluar…" : "Keluar"}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
